import { useMemo, FC } from "react";
import { DateTime } from "luxon";
import {
  LoanPaymentStatus,
  LOSPlatform,
  useLoanDashboard,
} from "../../contexts";
import { formatAmount } from "../../utils";
import { ArrowLeftIcon } from "../../assets/icons";
import { LoanDashboardFormCard } from "./LoanDashboardForm";
import LoanPaymentScheduleTable from "./LoanPaymentScheduleTable";
import { useLoanApplicationPayments } from "../../hooks";
import { useLoanApplicationPaymentsEnabled } from "../../hooks/useLoanApplicationPaymentsEnabled";
import { LoanProductType } from "../../hooks/api/types";
import LoanPaymentSchedule from "./LoanPaymentScheduleBuyNowPayLater/LoanPaymentSchedule";
import LoanDrawdownRequestsTable from "./LoanDrawdownRequestsTable/LoanDrawdownRequestsTable";
import { useLoanApplicationCreditFacilityEnabled } from "../../hooks/useLoanApplicationCreditFacilityEnabled";
import { useDpConfig } from "../../contexts/dpConfig";
import { LoanDashboardProgress } from "./LoanDashboardProgress";

const LoanPaymentScheduleForm: FC = () => {
  const {
    state: { loans, currentLoanApplicationId: loanId },
    actions: { update },
  } = useLoanDashboard();

  const {
    state: { products },
  } = useDpConfig();

  const [loan, _] = useMemo(() => {
    const _loanInd = loans.findIndex((e) => e.id === loanId);
    if (_loanInd > -1) {
      const _loan = loans[_loanInd];
      return [_loan, _loanInd];
    }
    return [undefined, _loanInd];
  }, [loans, loanId]);

  const productConfig = useMemo(() => {
    if (!loan) {
      return undefined;
    }
    return products.find((product) => product.id === loan.loanProductId);
  }, [products, loan]);

  const loanPaymentsEnabled = useLoanApplicationPaymentsEnabled(loan);

  const { data: loanPayments = [] } = useLoanApplicationPayments(
    loanId,
    loanPaymentsEnabled
  );

  const isCreditFacility = useLoanApplicationCreditFacilityEnabled(loan);

  const { amount, paidAmount, remainingAmount } = useMemo(() => {
    // either use the values returned in the loan, or calculate them based on the loan payments.
    if (loan?.amount && loan?.paidAmount && loan?.remainingAmount) {
      return {
        amount: loan.amount,
        paidAmount: loan.paidAmount,
        remainingAmount: loan.remainingAmount,
      };
    }
    return loanPayments.reduce(
      (acc, item) => {
        const amountToAdd = item.amount;
        const paidAmountToAdd =
          item?.status === LoanPaymentStatus.PAID ? item.amount : 0;
        const remainingAmountToAdd =
          item?.status !== LoanPaymentStatus.PAID ? item.amount : 0;

        return {
          amount: acc.amount + amountToAdd,
          paidAmount: acc.paidAmount + paidAmountToAdd,
          remainingAmount: acc.remainingAmount + remainingAmountToAdd,
        };
      },
      { amount: 0, paidAmount: 0, remainingAmount: 0 }
    );
  }, [loanPayments, loan]);

  const completion = useMemo(() => {
    if (!paidAmount) return 100;
    if (!remainingAmount) return 0;

    return (
      100 -
      Math.round((paidAmount / (paidAmount + remainingAmount)) * 10000) / 100
    );
  }, [amount, paidAmount, remainingAmount]);
  const loanInput = useMemo(() => {
    if (loan?.loanType === LoanProductType.FIXED_TERM) {
      return loan?.fixedTermLoan;
    } else if (loan?.loanType === LoanProductType.INVOICE_DISCOUNTING) {
      return loan?.invoiceDiscountingLoan?.invoices?.[0];
    } else if (loan?.loanType === LoanProductType.PAYABLE_FINANCING) {
      return loan?.payableFinancingLoan?.invoices?.[0];
    } else if (loan?.loanType === LoanProductType.REVENUE_BASED_FINANCING) {
      return loan?.revenueBasedLoan;
    } else if (loan?.loanType === LoanProductType.POS_REVENUE_BASED_FINANCING) {
      return loan?.posRevenueBasedLoan;
    } else if (loan?.loanType === LoanProductType.BNPL) {
      return loan?.buyNowPayLaterLoan;
    }
    return 0;
  }, [loan]);

  const { creditLimit, availableLimit } = useMemo(() => {
    if (
      loan?.loanType === LoanProductType.INVOICE_DISCOUNTING ||
      loan?.loanType === LoanProductType.PAYABLE_FINANCING
    ) {
      return {
        creditLimit: loanInput?.loanAmount || 0,
        availableLimit: loan?.availableLimit || 0,
      };
    }
    return { creditLimit: 0, availableLimit: 0 };
  }, [loan]);

  const availablePercentage = useMemo(() => {
    if (
      loan?.loanType === LoanProductType.INVOICE_DISCOUNTING ||
      loan?.loanType === LoanProductType.PAYABLE_FINANCING
    ) {
      return creditLimit > 0
        ? Math.round((availableLimit / creditLimit) * 100)
        : 0;
    }
    return Math.round((paidAmount / amount) * 10000) / 100; // Default calculation
  }, [loan, amount, paidAmount, creditLimit, availableLimit]);

  const progressLabel = useMemo(() => {
    if (isCreditFacility) {
      return "Available";
    }
    return "Outstanding";
  }, [loan?.loanType]);

  return (
    <div className="cx-grow cx-w-full">
      <div className="cx-flex cx-flex-row cx-justify-center cx-p-6 cx-border-stroke-primary cx-border-b">
        <div className="cx-w-full cx-max-w-[960px] cx-flex cx-flex-row cx-justify-between">
          <div className="cx-flex cx-flex-row cx-items-center cx-gap-x-4">
            <div
              className="cx-cursor-pointer"
              onClick={() => {
                update({
                  currentLoanApplicationId: "",
                  showPaymentSchedule: false,
                });
              }}
            >
              <ArrowLeftIcon />
            </div>
            <div className="cx-text-text-primary cx-font-semibold cx-text-lg">
              {productConfig?.config?.customProductAlias ||
                productConfig?.displayName ||
                ""}
              {isCreditFacility ? " - Credit Facility" : ""}
            </div>
          </div>
          <div className="sm:cx-flex cx-flex-row cx-items-center cx-gap-x-2">
            <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
              {`Loan #${loan?.externalId}`}
            </div>
            <div className="cx-hidden sm:cx-block cx-w-1 cx-h-1 cx-rounded-btn cx-bg-neutral-light" />
            {!isCreditFacility && (
              <>
                <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
                  {`${
                    loan?.losPlatform === LOSPlatform.CREDIBLEX
                      ? loan.interestRate
                      : loan?.loanOfferInterestRate || 0
                  }% Interest`}
                </div>
                <div className="cx-hidden sm:cx-block cx-w-1 cx-h-1 cx-rounded-btn cx-bg-neutral-light" />
              </>
            )}
            <div className="cx-text-text-secondary cx-font-medium cx-text-sm">
              Issued on{" "}
              {DateTime.fromISO(loan?.createdAt ?? "").toFormat("dd LLL yyyy")}
            </div>
          </div>
        </div>
      </div>
      {loan ? (
        <div className="cx-grow cx-w-full cx-max-w-[960px] cx-mx-auto">
          <div className="cx-w-full cx-flex cx-flex-row cx-justify-center cx-my-10 cx-max-h-[120px] cx-overflow-clip">
            <LoanDashboardProgress
              value={isCreditFacility ? availablePercentage : completion}
              size="large"
              label={progressLabel}
              invertFill={isCreditFacility}
            />
          </div>
          <div className="cx-w-full sm:cx-flex cx-flex-wrap cx-items-center cx-py-4 cx-px-2 cx-mb-6 sm:cx-divide-x sm:cx-divide-neutral-light cx-rounded-xl cx-border-stroke-primary cx-border">
            {(() => {
              // Define card data based on loan type
              const cardData = isCreditFacility
                ? [
                    {
                      label: "Credit limit",
                      value: creditLimit,
                      showCurrency: true,
                    },
                    {
                      label: "Available limit",
                      value: availableLimit,
                      showCurrency: true,
                    },
                    {
                      label: "Outstanding amount",
                      value: remainingAmount,
                      showCurrency: true,
                    },
                    {
                      label: "Interest rate on drawdown",
                      value: loan?.interestRate || 0,
                      suffix: "%",
                      showCurrency: false,
                    },
                  ]
                : [
                    {
                      label:
                        loan.losPlatform === LOSPlatform.CREDIBLEX
                          ? "Approved Amount"
                          : "Original loan amount",
                      value:
                        loan.losPlatform === LOSPlatform.CREDIBLEX
                          ? loan.approvedAmount || loan.amount
                          : loanInput?.loanAmount,
                      showCurrency: true,
                    },
                    {
                      label: "Outstanding balance",
                      value: remainingAmount,
                      showCurrency: true,
                    },
                    {
                      label: "Duration",
                      value: loanInput?.duration,
                      suffix: loanInput?.durationType,
                      showCurrency: false,
                    },
                  ];

              // Render the cards using the data
              return cardData.map((card, index) => (
                <LoanDashboardFormCard key={index} label={card.label}>
                  <div className="cx-grow cx-flex cx-items-center cx-justify-center cx-gap-x-1">
                    {card.showCurrency && (
                      <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                        <div className="cx-font-aed-font"> D </div>
                      </div>
                    )}
                    <div className="cx-text-text-primary cx-font-bold cx-text-xl">
                      {card.showCurrency
                        ? formatAmount(card.value)
                        : card.value}
                    </div>
                    {card.suffix && (
                      <div className="cx-text-text-primary cx-font-medium cx-text-xl">
                        {card.suffix}
                      </div>
                    )}
                  </div>
                </LoanDashboardFormCard>
              ));
            })()}
          </div>
          {loan.loanType === LoanProductType.BNPL ? (
            <LoanPaymentSchedule loan={loan} />
          ) : isCreditFacility ? (
            <LoanDrawdownRequestsTable loan={loan} />
          ) : (
            <LoanPaymentScheduleTable loan={loan} />
          )}
        </div>
      ) : (
        <div className="cx-grow cx-w-full cx-max-w-[960px]">
          <div className="cx-text-text-primary cx-font-semibold cx-text-lg cx-mr-4">
            Invalid Loan Selection
          </div>
        </div>
      )}
    </div>
  );
};

export default LoanPaymentScheduleForm;
